import React, { ComponentType } from 'react';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import { iListClinicalDocument } from '../interface';
import { iClinicalDocs, iStore } from '~/domain/interfaces/models';

import { pageProps } from '~/presentation/roles/professional/pages/clinicalDocument';
import { getLocale } from '~/utils/getLocale';
import { format } from 'date-fns';

/**
 * Mapping of the clinicalDocs list data.
 *
 * @param Component component to connect.
 */
export function ConnectClinicalDocs<P>(
  Component: ComponentType<P & pageProps>,
): React.FC<P> {
  const Render: React.FC<P> = ({ ...rest }) => {
    const clinicalDocs = useSelector((store: iStore) => store.clinicalDocs);

    /* eslint no-param-reassign: "error" */

    Component.defaultProps = {
      data: _.orderBy(
        // Default descending order for date
        mapClinicalDocsData(clinicalDocs),
        item => {
          const splitDate = item.date.split('/');
          const internationalDate = splitDate.reverse().join('-');

          return new Date(internationalDate).getTime();
        },
        ['desc'],
      ),
      loading: clinicalDocs.loading,
      ...(rest as P),
    };

    return <Component {...(rest as P & pageProps)} />;
  };

  return Render;
}

/**
 * component data mapper
 * @param clinicalDocs data.
 */
const mapClinicalDocsData = (
  clinicalDocs: iClinicalDocs,
): iListClinicalDocument[] => {
  const arr: iListClinicalDocument[] = [];
  clinicalDocs.results.forEach(item => {
    const date = new Date(item.document!.sent);

    const fullName = item.patientExternal?.fullname
      ? item.patientExternal.fullname
      : `${item.patient.firstName} ${item.patient.lastName}`;

    const time = date.toTimeString().substring(0, 9);

    const formattedDate = format(date, 'dd/MM/yyyy');

    arr.push({
      id: item.document!.id.toString(),
      avatar: item.patient.avatar,
      fullName,
      userId: item.patient.id,
      status: item.patient.status,
      specialty: item.specialty.name,
      date: formattedDate,
      statusSignature: item!.document!.status,
      time: time!,
      origin: item!.document!.origin,
      clinicalDocument: item.type.name,
    });
  });

  return arr;
};
